/**
 * NotificationItem
 *
 * One row in the notifications dropdown (and the full Notifications page).
 *
 * Layout:
 *   [icon disc] · title (+ unread dot) · body · key/value rows · timestamp
 *
 * The icon disc colour is keyed off the notification type — assignments
 * read amber, system alerts red, etc. — so a learner can scan the list
 * and recognise category at a glance without reading every word. The
 * body parses "Label: Value" lines into compact key/value rows so dates
 * surface as structured facts instead of a wall of preformatted text.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, ListItem, ListItemButton } from '../ui';
import {
  BookmarkIcon,
  CheckCircleIcon,
  DateRangeIcon,
  EmailIcon,
  ErrorIcon,
  InfoIcon,
  NotificationsIcon,
  SchoolIcon,
  WarningIcon,
} from '../icons';
import {
  Notification,
  NotificationType,
} from '../../features/notifications/notificationsSlice';
import {
  handleNotificationClick,
  getNotificationUrl,
} from '../../utils/notificationNavigation';
import { formatDistanceToNow } from 'date-fns';

interface NotificationItemProps {
  condensed?: boolean;
  notification: Notification;
  onClick?: () => void;
}

interface TypeStyle {
  icon: React.ReactNode;
  /** Soft tinted background for the icon disc. */
  bg: string;
  /** Foreground color for the icon glyph + accent dot. */
  fg: string;
}

/**
 * Resolves a soft-tinted disc style for the given notification type.
 * Picks brand cyan for the generic categories and warmer / cooler
 * accents for the categories with their own semantic.
 */
function styleForType(type: NotificationType | undefined): TypeStyle {
  switch (type) {
    case NotificationType.AssignmentStart:
      return { icon: <BookmarkIcon sx={{ fontSize: 20 }} />, bg: 'warning.softBg', fg: 'warning.softText' };
    case NotificationType.Reminder24h:
      return { icon: <DateRangeIcon sx={{ fontSize: 20 }} />, bg: 'warning.softBg', fg: 'warning.softText' };
    case NotificationType.Overdue:
      return { icon: <WarningIcon sx={{ fontSize: 20 }} />, bg: 'error.softBg', fg: 'error.dark' };
    case NotificationType.Announcement:
      return { icon: <InfoIcon sx={{ fontSize: 20 }} />, bg: 'rgba(var(--brand-rgb), 0.18)', fg: 'rgb(var(--brand-rgb))' };
    case NotificationType.CourseUpdate:
      return { icon: <SchoolIcon sx={{ fontSize: 20 }} />, bg: 'rgba(var(--brand-rgb), 0.18)', fg: 'rgb(var(--brand-rgb))' };
    case NotificationType.GradePosted:
      return { icon: <CheckCircleIcon sx={{ fontSize: 20 }} />, bg: 'success.softBg', fg: 'success.main' };
    case NotificationType.MessageReceived:
      return { icon: <EmailIcon sx={{ fontSize: 20 }} />, bg: 'rgba(var(--brand-rgb), 0.18)', fg: 'rgb(var(--brand-rgb))' };
    case NotificationType.SystemAlert:
      return { icon: <ErrorIcon sx={{ fontSize: 20 }} />, bg: 'error.softBg', fg: 'error.dark' };
    default:
      return { icon: <NotificationsIcon sx={{ fontSize: 20 }} />, bg: 'grey.100', fg: 'text.secondary' };
  }
}

/**
 * Splits the body into a leading paragraph + an array of structured
 * `Label: Value` rows. Lines like "Start Date: May 28, 2026" become
 * { label: "Start Date", value: "May 28, 2026" }; everything else
 * folds into the paragraph so we never silently drop content.
 */
function parseBody(raw: string | undefined): {
  paragraph: string;
  rows: { label: string; value: string }[];
} {
  if (!raw) return { paragraph: '', rows: [] };
  const lines = raw
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l.length > 0);

  const rows: { label: string; value: string }[] = [];
  const otherLines: string[] = [];
  // Only treat short "Label: value" pairs as structured rows so the
  // intro sentence doesn't get carved up just because it contains a
  // colon. Caps the label at 30 chars and requires a non-empty value.
  const KV_RE = /^([A-Za-z][\w ]{0,29}):\s+(.+)$/;
  for (const line of lines) {
    const m = line.match(KV_RE);
    if (m) {
      rows.push({ label: m[1].trim(), value: m[2].trim() });
    } else {
      otherLines.push(line);
    }
  }
  return { paragraph: otherLines.join('\n'), rows };
}

const NotificationItem: React.FC<NotificationItemProps> = ({
  condensed,
  notification,
  onClick,
}) => {
  const navigate = useNavigate();
  const hasLink = !!getNotificationUrl(notification);
  const unread = !notification.isRead;
  const typeStyle = styleForType(notification.notificationType);
  const { paragraph, rows } = parseBody(notification.textBody);

  const handleClick = () => {
    if (onClick) onClick();
    handleNotificationClick(notification, navigate);
  };

  const timeAgo = formatDistanceToNow(new Date(notification.createdAt), {
    addSuffix: true,
  });

  return (
    <ListItem disablePadding>
      <ListItemButton
        onClick={handleClick}
        sx={{
          py: 1.5,
          px: 2,
          alignItems: 'flex-start',
          gap: 1.5,
          cursor: hasLink ? 'pointer' : 'default',
          borderBottom: '1px solid',
          borderBottomColor: 'divider',
          // Soft unread tint instead of a harsh 4px coloured border —
          // reads as "look here" without dominating the row.
          bgcolor: unread ? 'rgba(var(--brand-rgb), 0.06)' : 'transparent',
          '&:hover': {
            bgcolor: hasLink
              ? unread
                ? 'rgba(var(--brand-rgb), 0.12)'
                : 'action.hover'
              : unread
                ? 'rgba(var(--brand-rgb), 0.06)'
                : 'transparent',
          },
          '&:last-of-type': { borderBottom: 'none' },
        }}
      >
        {/* Icon disc — categorical signal that survives a quick scan. */}
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            bgcolor: typeStyle.bg,
            color: typeStyle.fg,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            mt: 0.25,
          }}
        >
          {typeStyle.icon}
        </Box>

        {/* Content column */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* Title row — unread badge on the right keeps the title flush
              left and the dot from interrupting the read. */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.5 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: unread ? 700 : 500,
                flex: 1,
                minWidth: 0,
                lineHeight: 1.3,
                wordBreak: 'break-word',
              }}
            >
              {notification.title}
            </Typography>
            {unread && (
              <Box
                aria-label="Unread"
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: 'primary.main',
                  flexShrink: 0,
                  mt: 0.75,
                }}
              />
            )}
          </Box>

          {paragraph && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                whiteSpace: 'pre-line',
                wordBreak: 'break-word',
                mb: rows.length > 0 ? 0.75 : 0.25,
                // Two-line clamp in condensed mode keeps the dropdown
                // tight; the full page rendering lets content flow.
                ...(condensed
                  ? {
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }
                  : {}),
              }}
            >
              {paragraph}
            </Typography>
          )}

          {/* Structured Label: Value rows — small dated facts read as
              compact metadata rather than another paragraph. */}
          {rows.length > 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, mb: 0.5 }}>
              {rows.map((r, i) => (
                <Box key={`${r.label}-${i}`} sx={{ display: 'flex', gap: 0.5 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontWeight: 600, flexShrink: 0 }}
                  >
                    {r.label}:
                  </Typography>
                  <Typography variant="caption" color="text.primary">
                    {r.value}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}

          <Typography
            variant="caption"
            color="text.disabled"
            sx={{ fontSize: '0.7rem' }}
          >
            {timeAgo}
          </Typography>
        </Box>
      </ListItemButton>
    </ListItem>
  );
};

export default NotificationItem;
